import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import type {StockDevelopmentResponse} from '../api'
import {
  getStockDevelopmentDataPerMaterial,
  getStockDevelopmentForRange,
  StockDevelopmentData
} from '../selectors'

export const StockDevelopmentQueryKey = 'stockDevelopment'

const STOCK_DEVELOPMENT_END_HOURS_PADDING = 2
type MomentRange = [start: Moment, end: Moment]

interface StockDevelopmentParams {
  materialIds: number[] | undefined
  timeframe: MomentRange
  selectedRange?: MomentRange
  scheduleId?: number
  useOptimizedSchedule?: boolean
}

export const useStockDevelopmentQuery = ({
  materialIds = [],
  timeframe,
  selectedRange,
  scheduleId,
  useOptimizedSchedule = false
}: StockDevelopmentParams) => {
  const {plantId: plantCode} = useUpmPlantContext()

  const from = timeframe[0].toISOString()
  // We need an additional hour of stock levels data to display material orders correctly
  const to = timeframe[1]
    .clone()
    .add(STOCK_DEVELOPMENT_END_HOURS_PADDING, 'hour')
    .startOf('hour')
    .toISOString()

  const selector = useCallback(
    (stockDevelopment: StockDevelopmentResponse | undefined) => {
      if (!stockDevelopment) {
        return undefined
      }
      const selectedStockDevelopment = selectedRange
        ? getStockDevelopmentForRange(stockDevelopment, selectedRange)
        : stockDevelopment

      return getStockDevelopmentDataPerMaterial(selectedStockDevelopment)
    },
    [selectedRange]
  )

  return useQuery<
    StockDevelopmentResponse | undefined,
    AxiosError,
    Record<string, StockDevelopmentData> | undefined
  >({
    queryKey: [
      StockDevelopmentQueryKey,
      {plantCode, materialIds, from, to, scheduleId, useOptimizedSchedule}
    ],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<StockDevelopmentResponse>(
        `plants/${plantCode}/schedules/kpi/stock-development`,
        {
          params: {
            from,
            to,
            materialIds: materialIds.join(','),
            useOptimizedSchedule,
            scheduleId
          },
          signal
        }
      )
      return response.data
    },
    enabled: Boolean(materialIds.length),
    select: selector,
    throwOnError: true
  })
}
